import React from 'react'
import ReactTable from 'react-table'
import styled from 'styled-components'
import { number, func, object, bool } from 'prop-types'
import Text from './Text'
import {
  WHITE,
  SHADOW_GREY,
  VERMILION,
  METAL,
  SILVER,
  LIGHT_GREY,
} from '../../constants'
import ErrorBoundary from './ErrorBoundary'
import { structuredTableShape, structuredDataShape } from '../../types'
import Dropdown from './Dropdown'
import '@fontsource/dm-sans' // Defaults to weight 400

const MainTable = styled(ReactTable)`
  border: none !important;
  color: ${METAL};
  font-family: 'Dm Sans';
  overflow: auto contain;

  .rt-thead .rt-tr {
    padding: 10px ${({ rowPadding }) => (rowPadding ? '10px' : '0')};
    font-weight: bold;
    text-align: left !important;
    .rt-th {
      border: none;
    }
    .rt-resizable-header-content {
      color: ${VERMILION};
      display: flex;
    }
  }

  .rt-tbody {
  }

  .rt-tbody .rt-td-expandable,
  .rt-tbody .rt-tr-group-expandable {
    border-radius: 5px;
    box-shadow: ${SHADOW_GREY};
  }

  .rt-tbody
    .rt-td-expandable
    ~ .ReactTable
    .rt-tbody
    > .rt-tr-group:nth-last-child(1) {
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  .rt-tbody
    .rt-td-expandable
    ~ .ReactTable
    .rt-tbody
    > .rt-tr-group:nth-child(1) {
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
  }

  .rt-tbody .rt-td {
    border-right: none !important;
    text-overflow: ellipsis !important;
    display: flex;
    align-items: center;
  }

  ${({ rightAlignLastColumn }) =>
    rightAlignLastColumn &&
    `.rt-tbody .rt-td:last-child {
      display: flex;
      justify-content: flex-end;
    }
`}

  .rt-tbody > .rt-tr-group {
    margin-bottom: 10px;
    background: ${WHITE};
    box-shadow: ${SHADOW_GREY};
    line-height: 1.8;
    ${({ rowPadding }) => rowPadding && 'padding: 0 10px'};
  }

  .rt-tbody .rt-tr-group {
    border-bottom: solid 0px rgba(0, 0, 0, 0.05) !important;
  }
`

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const InnerPaginationWrapper = styled.div`
  display: flex;
  height: 45px;
`

const PaginationWrapper = styled.div`
  display: flex;
  gap: 10px
  align-items: center;
`

const TableWrapper = styled.div`
  .ReactTable .rt-noData {
    z-index: 0 !important;
    background: #00000000;
    border-radius: 5px;
    color: ${SILVER};
    margin-top: 20px;
    font-weight: bold;
  }
`

const ButtonWrapper = styled.div`
  transform: ${({ isPrev }) => (isPrev ? 'rotate(-135deg)' : 'rotate(45deg)')};
  padding: 9px;
  box-shadow: 3px -3px 0 0 ${VERMILION};
  margin: 14px;
`

const StyledButton = styled.button`
  border: none;
  background: transparent;
  position: absolute;
  width: 100%;
  height: 100%;
  bottom: 8px;
  ${({ disabled }) => !disabled && 'cursor: pointer'};
  outline: none;
`

const PagesNumber = styled.div`
  display: inline-block;
  position: relative;
  top: 13px;
`

const PageSizeDropdownWrapper = styled.div`
  width: 100px;
  height: 55px;
  display: inline-block;
`

const pageSizeOptions = [
  { id: 10, name: '10' },
  { id: 50, name: '50' },
  { id: 100, name: '100' },
  { id: 250, name: '250' },
  { id: 500, name: '500' },
]

function showAdvancedPagination(
  page,
  pageTotal,
  pageSize,
  selectNextPage,
  selectPreviousPage,
  setPageSize,
  t
) {
  return (
    <PaginationWrapper>
      <PageSizeDropdownWrapper>
        <Dropdown
          label={t('show_rows')}
          onChange={event => {
            setPageSize(event.id)
          }}
          options={pageSizeOptions}
          boundValue={{ id: pageSize, name: JSON.stringify(pageSize) }}
          isHigh
          noOptionsMinWidth={true}
        />
      </PageSizeDropdownWrapper>
      <InnerPaginationWrapper>
        <ButtonWrapper isPrev>
          <StyledButton
            onClick={selectPreviousPage}
            disabled={page === 0 || page === null}
          />
        </ButtonWrapper>
        <PagesNumber>
          <Text bold>{`${page + 1} of ${pageTotal} `}</Text>
        </PagesNumber>
        <ButtonWrapper>
          <StyledButton
            onClick={selectNextPage}
            disabled={page + 1 === pageTotal || pageTotal === null}
          />
        </ButtonWrapper>
      </InnerPaginationWrapper>
    </PaginationWrapper>
  )
}

function showSimplePagination(
  page,
  pageTotal,
  selectNextPage,
  selectPreviousPage
) {
  return (
    <InnerPaginationWrapper>
      <ButtonWrapper isPrev>
        <StyledButton
          onClick={selectPreviousPage}
          disabled={page === 0 || page === null}
        />
      </ButtonWrapper>
      <PagesNumber>
        <Text bold>{`${page + 1} of ${pageTotal} `}</Text>
      </PagesNumber>
      <ButtonWrapper>
        <StyledButton
          onClick={selectNextPage}
          disabled={page + 1 === pageTotal || pageTotal === null}
        />
      </ButtonWrapper>
    </InnerPaginationWrapper>
  )
}

const StructuredTable = ({
  columns,
  data,
  expanded,
  getTrGroupProps,
  getTrProps,
  onExpandedChange,
  page,
  pageSize,
  pageTotal,
  selectNextPage,
  selectPreviousPage,
  SubComponent,
  rightAlignLastColumn,
  rowPadding,
  displayTopContent,
  setPageSize,
  t,
  noPagination,
}) => {
  return (
    <ErrorBoundary>
      <Wrapper>
        {displayTopContent ? displayTopContent() : <div />}
        {!noPagination &&
          (setPageSize
            ? showAdvancedPagination(
                page,
                pageTotal,
                pageSize,
                selectNextPage,
                selectPreviousPage,
                setPageSize,
                t
              )
            : showSimplePagination(
                page,
                pageTotal,
                selectNextPage,
                selectPreviousPage
              ))}
      </Wrapper>
      <TableWrapper>
        <MainTable
          columns={columns}
          data={data}
          expanded={expanded}
          getTrGroupProps={getTrGroupProps}
          getTrProps={getTrProps}
          rightAlignLastColumn={rightAlignLastColumn}
          rowPadding={rowPadding}
          onExpandedChange={onExpandedChange}
          pageSize={pageSize}
          resizable={false}
          showPagination={false}
          sortable={false}
          SubComponent={SubComponent}
          minRows={1}
        />
      </TableWrapper>
    </ErrorBoundary>
  )
}

StructuredTable.propTypes = {
  columns: structuredTableShape,
  data: structuredDataShape,
  expanded: object,
  getTrProps: func,
  onExpandedChange: func,
  page: number,
  pageSize: number.isRequired,
  pageTotal: number,
  selectNextPage: func.isRequired,
  selectPreviousPage: func.isRequired,
  SubComponent: func,
  rightAlignLastColumn: bool,
  rowPadding: bool,
}
export default StructuredTable
